import { useState } from "react";
import { ShieldCheck, Loader2, ArrowLeft, Mail, Smartphone, RefreshCw } from "lucide-react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Étape 2FA de la page de connexion : code TOTP / e-mail ou code de secours
export default function TwoFactorStep({ challenge, onVerify, onBack, primary, onPrimary, light, inputBg, muted }) {
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resent, setResent] = useState("");
  const [devCode, setDevCode] = useState(challenge.dev_code || "");
  const isEmail = challenge.method === "email";

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try { await onVerify(code.trim(), remember); }
    catch (err) { setError(err.response?.data?.detail || "Code incorrect"); }
    setBusy(false);
  };

  const resend = async () => {
    setResent(""); setError("");
    try {
      const r = await axios.post(`${API}/auth/2fa/resend`, { challenge_id: challenge.challenge_id });
      setResent(r.data.email_sent ? "Nouveau code envoyé" : "SMTP non configuré — code affiché ci-dessous");
      if (r.data.dev_code) setDevCode(r.data.dev_code);
    } catch (err) { setError(err.response?.data?.detail || "Renvoi impossible"); }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="twofa-step">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${primary}22`, color: primary }}>
          {isEmail ? <Mail className="w-5 h-5" /> : <Smartphone className="w-5 h-5" />}
        </div>
        <div>
          <div className="font-bold">Vérification en deux étapes</div>
          <div className="text-xs" style={{ color: muted }}>
            {isEmail ? `Un code à 6 chiffres a été envoyé à ${challenge.email_hint}` : "Saisissez le code de votre application d'authentification"}
          </div>
        </div>
      </div>
      {isEmail && challenge.email_sent === false && !devCode && (
        <div className="text-xs p-2 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-400">L'e-mail n'a pas pu être envoyé. Utilisez un code de secours ou contactez un administrateur.</div>
      )}
      {devCode && (
        <div className="text-xs p-2 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-400" data-testid="twofa-dev-code">SMTP non configuré (environnement de test) — code : <b>{devCode}</b></div>
      )}
      <Input
        data-testid="twofa-code-input"
        autoFocus
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder="000000 ou code de secours"
        inputMode="text"
        autoComplete="one-time-code"
        className={`h-14 text-center text-2xl tracking-[0.4em] font-mono ${light ? "border-black/10 text-[#202020]" : "border-white/10 text-white"}`}
        style={{ background: inputBg }}
      />
      <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: muted }}>
        <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="w-4 h-4" style={{ accentColor: primary }} data-testid="twofa-remember" />
        Ne plus demander sur cet appareil pendant 30 jours
      </label>
      {error && <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 text-sm" data-testid="twofa-error">{error}</div>}
      {resent && <div className="text-xs text-green-400" data-testid="twofa-resent">{resent}</div>}
      <Button type="submit" disabled={busy || code.length < 6} className="w-full h-12 font-bold text-base rounded-lg" style={{ background: primary, color: onPrimary }} data-testid="twofa-submit-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />} Valider
      </Button>
      <div className="flex justify-between text-xs">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 hover:underline" style={{ color: muted }} data-testid="twofa-back"><ArrowLeft className="w-3 h-3" /> Retour</button>
        {isEmail && <button type="button" onClick={resend} className="inline-flex items-center gap-1 hover:underline" style={{ color: primary }} data-testid="twofa-resend"><RefreshCw className="w-3 h-3" /> Renvoyer le code</button>}
      </div>
    </form>
  );
}
